import { apiClient } from './apiClient';

export type PlanningCycle = {
  id: string;
  name: string;
  description?: string | null;
  kind: string;
  status: string;
  cancellationReason?: string | null;
  startDate: string;
  endDate: string;
  ownerUserId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
};

export type PlanningCycleUpsertPayload = {
  name: string;
  description?: string;
  kind: string;
  status: string;
  cancellationReason?: string;
  startDate: string;
  endDate: string;
  ownerUserId?: string;
};

export type PlanningSprint = {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  area: string;
  createdAt: string;
  updatedAt?: string | null;
};

export type PlanningSprintUpsertPayload = {
  title: string;
  startDate: string;
  endDate: string;
  area: string;
};

export type PlanningIndicator = {
  id: string;
  name: string;
  unit?: string | null;
  valueKind: string;
  targetValue?: number | null;
  currentValue?: number | null;
  qualitativeTarget?: string | null;
  qualitativeCurrent?: string | null;
};

export type PlanningObjective = {
  id: string;
  cycleId: string;
  kind: string;
  title: string;
  description?: string | null;
  status: string;
  cancellationReason?: string | null;
  sortOrder: number;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  ownerUserId?: string | null;
  indicators: PlanningIndicator[];
  createdAt: string;
  updatedAt?: string | null;
};

export type PlanningObjectiveUpsertPayload = {
  kind: string;
  title: string;
  description?: string;
  status: string;
  cancellationReason?: string;
  sortOrder: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  ownerUserId?: string;
  indicators: Array<{
    name: string;
    unit?: string;
    valueKind: string;
    targetValue?: number;
    currentValue?: number;
    qualitativeTarget?: string;
    qualitativeCurrent?: string;
  }>;
};

export type PlanningWorkItemEvidence = {
  id: string;
  title: string;
  description?: string | null;
  externalUrl?: string | null;
  documentId?: string | null;
  storagePath?: string | null;
  addedByUserId?: string | null;
  createdAt: string;
};

export type PlanningWorkItem = {
  id: string;
  kind: string;
  title: string;
  description?: string | null;
  status: string;
  cancellationReason?: string | null;
  priority: string;
  sortOrder: number;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  cycleId?: string | null;
  sprintId?: string | null;
  objectiveId?: string | null;
  parentWorkItemId?: string | null;
  rootWorkItemId?: string | null;
  supersedesWorkItemId?: string | null;
  deadlineChangeReason?: string | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  executorUserId?: string | null;
  teamId?: string | null;
  tags?: string | null;
  executionStartedAt?: string | null;
  executionFinishedAt?: string | null;
  fulfillmentResult?: string | null;
  evidences: PlanningWorkItemEvidence[];
  areas?: string[];
  children: PlanningWorkItem[];
  createdAt: string;
  updatedAt?: string | null;
};

export type PlanningWorkItemUpsertPayload = {
  kind: string;
  title: string;
  description?: string;
  status?: string;
  cancellationReason?: string;
  priority: string;
  sortOrder?: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  cycleId?: string;
  sprintId?: string;
  objectiveId?: string;
  parentWorkItemId?: string;
  ownerUserId?: string;
  teamId?: string;
  tags?: string;
  areas?: string[];
};

export type PlanningTaskStartExecutionPayload = {
  executorUserId: string;
  startedAt?: string;
};

export type PlanningTaskFinishExecutionPayload = {
  fulfillmentResult: string;
  finishedAt?: string;
};

export type PlanningWorkItemEvidenceFormPayload = {
  title: string;
  description: string;
  externalUrl?: string;
  image?: File | null;
  removeImage?: boolean;
};

export type PlanningWorkItemEvidenceImageUrl = {
  evidenceId: string;
  url: string;
  expiresAtUtc: string;
};

export type PlanningDeleteImpact = {
  directChildrenCount: number;
  totalDescendantsCount: number;
  requiresTransferDecision: boolean;
};

export type PlanningDeletePayload = {
  transferChildrenToId?: string;
};

export type PlanningCancelPayload = {
  cancellationReason: string;
};

export type PlanningDuplicateCascadePayload = {
  titleSuffix?: string;
  targetCycleId?: string;
  includeObjectives?: boolean;
  includeMetas?: boolean;
  includeMarcos?: boolean;
  includeTasks?: boolean;
};

export type PlanningWorkItemListFilters = {
  kind?: string;
  status?: string;
  cycleId?: string;
  objectiveId?: string;
  parentWorkItemId?: string;
  sprintId?: string;
  backlogOnly?: boolean;
};

/** @deprecated Use PlanningWorkItem */
export type PlanningTask = PlanningWorkItem;

/** @deprecated Use PlanningWorkItemUpsertPayload */
export type PlanningTaskUpsertPayload = PlanningWorkItemUpsertPayload;

/** @deprecated Use PlanningWorkItemEvidence */
export type PlanningTaskEvidence = PlanningWorkItemEvidence;

/** @deprecated Use PlanningWorkItemEvidenceFormPayload */
export type PlanningTaskEvidenceUpsertPayload = PlanningWorkItemEvidenceFormPayload;

/** @deprecated Use PlanningWorkItemListFilters */
export type PlanningTaskListFilters = PlanningWorkItemListFilters;

export async function listPlanningCycles(): Promise<PlanningCycle[]> {
  const response = await apiClient.get<PlanningCycle[]>('/v1/strategic-planning/cycles');
  return response.data;
}

export async function createPlanningCycle(
  payload: PlanningCycleUpsertPayload,
): Promise<PlanningCycle> {
  const response = await apiClient.post<PlanningCycle>('/v1/strategic-planning/cycles', payload);
  return response.data;
}

export async function updatePlanningCycle(
  cycleId: string,
  payload: PlanningCycleUpsertPayload,
): Promise<PlanningCycle> {
  const response = await apiClient.put<PlanningCycle>(
    `/v1/strategic-planning/cycles/${cycleId}`,
    payload,
  );
  return response.data;
}

export async function getCycleDeleteImpact(cycleId: string): Promise<PlanningDeleteImpact> {
  const response = await apiClient.get<PlanningDeleteImpact>(
    `/v1/strategic-planning/cycles/${cycleId}/delete-impact`,
  );
  return response.data;
}

export async function deletePlanningCycle(
  cycleId: string,
  payload: PlanningDeletePayload = {},
): Promise<void> {
  await apiClient.delete(`/v1/strategic-planning/cycles/${cycleId}`, { data: payload });
}

export async function listPlanningSprints(area?: string): Promise<PlanningSprint[]> {
  const response = await apiClient.get<PlanningSprint[]>('/v1/strategic-planning/sprints', {
    params: area ? { area } : undefined,
  });
  return response.data;
}

export async function createPlanningSprint(
  payload: PlanningSprintUpsertPayload,
): Promise<PlanningSprint> {
  const response = await apiClient.post<PlanningSprint>('/v1/strategic-planning/sprints', payload);
  return response.data;
}

export async function updatePlanningSprint(
  sprintId: string,
  payload: PlanningSprintUpsertPayload,
): Promise<PlanningSprint> {
  const response = await apiClient.put<PlanningSprint>(
    `/v1/strategic-planning/sprints/${sprintId}`,
    payload,
  );
  return response.data;
}

export async function deletePlanningSprint(sprintId: string): Promise<void> {
  await apiClient.delete(`/v1/strategic-planning/sprints/${sprintId}`);
}

export async function listPlanningObjectives(
  cycleId?: string,
  kind?: string,
): Promise<PlanningObjective[]> {
  const response = await apiClient.get<PlanningObjective[]>(
    '/v1/strategic-planning/objectives',
    {
      params: {
        ...(cycleId ? { cycleId } : {}),
        ...(kind ? { kind } : {}),
      },
    },
  );
  return response.data;
}

export async function createPlanningObjective(
  cycleId: string,
  payload: PlanningObjectiveUpsertPayload,
): Promise<PlanningObjective> {
  const response = await apiClient.post<PlanningObjective>(
    `/v1/strategic-planning/cycles/${cycleId}/objectives`,
    payload,
  );
  return response.data;
}

export async function updatePlanningObjective(
  objectiveId: string,
  payload: PlanningObjectiveUpsertPayload,
): Promise<PlanningObjective> {
  const response = await apiClient.put<PlanningObjective>(
    `/v1/strategic-planning/objectives/${objectiveId}`,
    payload,
  );
  return response.data;
}

export async function getObjectiveDeleteImpact(objectiveId: string): Promise<PlanningDeleteImpact> {
  const response = await apiClient.get<PlanningDeleteImpact>(
    `/v1/strategic-planning/objectives/${objectiveId}/delete-impact`,
  );
  return response.data;
}

export async function deletePlanningObjective(
  objectiveId: string,
  payload: PlanningDeletePayload = {},
): Promise<void> {
  await apiClient.delete(`/v1/strategic-planning/objectives/${objectiveId}`, { data: payload });
}

export async function getWorkItemDeleteImpact(workItemId: string): Promise<PlanningDeleteImpact> {
  const response = await apiClient.get<PlanningDeleteImpact>(
    `/v1/strategic-planning/work-items/${workItemId}/delete-impact`,
  );
  return response.data;
}

export async function deletePlanningWorkItem(
  workItemId: string,
  payload: PlanningDeletePayload = {},
): Promise<void> {
  await apiClient.delete(`/v1/strategic-planning/work-items/${workItemId}`, { data: payload });
}

export async function listObjectiveWorkItemTree(objectiveId: string): Promise<PlanningWorkItem[]> {
  const response = await apiClient.get<PlanningWorkItem[]>(
    `/v1/strategic-planning/objectives/${objectiveId}/work-items/tree`,
  );
  return response.data;
}

export async function listPlanningWorkItems(
  filters: PlanningWorkItemListFilters = {},
): Promise<PlanningWorkItem[]> {
  const response = await apiClient.get<PlanningWorkItem[]>('/v1/strategic-planning/work-items', {
    params: filters,
  });
  return response.data;
}

export async function getPlanningWorkItem(workItemId: string): Promise<PlanningWorkItem> {
  const response = await apiClient.get<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}`,
  );
  return response.data;
}

export async function createPlanningWorkItem(
  payload: PlanningWorkItemUpsertPayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    '/v1/strategic-planning/work-items',
    payload,
  );
  return response.data;
}

export async function updatePlanningWorkItem(
  workItemId: string,
  payload: PlanningWorkItemUpsertPayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.put<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}`,
    payload,
  );
  return response.data;
}

export async function replacePlanningWorkItemAreas(
  workItemId: string,
  areas: string[],
): Promise<PlanningWorkItem> {
  const response = await apiClient.put<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/areas`,
    { areas },
  );
  return response.data;
}

export type PlanningWorkItemExtendDeadlinePayload = {
  newPlannedEndDate: string;
  reason: string;
};

export async function extendPlanningWorkItemDeadline(
  workItemId: string,
  payload: PlanningWorkItemExtendDeadlinePayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/extend-deadline`,
    payload,
  );
  return response.data;
}

export async function assignPlanningWorkItem(
  workItemId: string,
  payload: { ownerUserId: string },
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/assign`,
    payload,
  );
  return response.data;
}

export async function startPlanningWorkItemExecution(
  workItemId: string,
  payload: PlanningTaskStartExecutionPayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/start-execution`,
    payload,
  );
  return response.data;
}

export async function finishPlanningWorkItemExecution(
  workItemId: string,
  payload: PlanningTaskFinishExecutionPayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/finish-execution`,
    payload,
  );
  return response.data;
}

function toEvidenceFormData(payload: PlanningWorkItemEvidenceFormPayload): FormData {
  const formData = new FormData();
  formData.append('title', payload.title);
  formData.append('description', payload.description);
  if (payload.externalUrl) formData.append('externalUrl', payload.externalUrl);
  if (payload.removeImage) formData.append('removeImage', 'true');
  if (payload.image) formData.append('image', payload.image);
  return formData;
}

export async function addPlanningWorkItemEvidence(
  workItemId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.post<PlanningWorkItemEvidence>(
    `/v1/strategic-planning/work-items/${workItemId}/evidences`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function updatePlanningWorkItemEvidence(
  workItemId: string,
  evidenceId: string,
  payload: PlanningWorkItemEvidenceFormPayload,
): Promise<PlanningWorkItemEvidence> {
  const response = await apiClient.put<PlanningWorkItemEvidence>(
    `/v1/strategic-planning/work-items/${workItemId}/evidences/${evidenceId}`,
    toEvidenceFormData(payload),
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function fetchPlanningWorkItemEvidenceImageUrl(
  workItemId: string,
  evidenceId: string,
): Promise<PlanningWorkItemEvidenceImageUrl> {
  const response = await apiClient.get<PlanningWorkItemEvidenceImageUrl>(
    `/v1/strategic-planning/work-items/${workItemId}/evidences/${evidenceId}/image-url`,
  );
  return response.data;
}

export async function deletePlanningWorkItemEvidence(
  workItemId: string,
  evidenceId: string,
): Promise<void> {
  await apiClient.delete(
    `/v1/strategic-planning/work-items/${workItemId}/evidences/${evidenceId}`,
  );
}

export async function cancelPlanningWorkItem(
  workItemId: string,
  payload: PlanningCancelPayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/cancel`,
    payload,
  );
  return response.data;
}

export async function duplicatePlanningCycle(
  cycleId: string,
  payload: PlanningDuplicateCascadePayload,
): Promise<PlanningCycle> {
  const response = await apiClient.post<PlanningCycle>(
    `/v1/strategic-planning/cycles/${cycleId}/duplicate`,
    payload,
  );
  return response.data;
}

export async function duplicatePlanningObjective(
  objectiveId: string,
  payload: PlanningDuplicateCascadePayload,
): Promise<PlanningObjective> {
  const response = await apiClient.post<PlanningObjective>(
    `/v1/strategic-planning/objectives/${objectiveId}/duplicate`,
    payload,
  );
  return response.data;
}

export async function duplicatePlanningWorkItem(
  workItemId: string,
  payload: PlanningDuplicateCascadePayload,
): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/duplicate`,
    payload,
  );
  return response.data;
}

export async function duplicatePlanningTask(workItemId: string): Promise<PlanningWorkItem> {
  const response = await apiClient.post<PlanningWorkItem>(
    `/v1/strategic-planning/work-items/${workItemId}/duplicate-task`,
  );
  return response.data;
}

export async function listPlanningTasks(
  filters: PlanningWorkItemListFilters = {},
): Promise<PlanningWorkItem[]> {
  return listPlanningWorkItems({ ...filters, kind: 'Tarefa' });
}

export const getPlanningTask = getPlanningWorkItem;
export const createPlanningTask = (payload: PlanningWorkItemUpsertPayload) =>
  createPlanningWorkItem({ ...payload, kind: 'Tarefa' });
export const updatePlanningTask = updatePlanningWorkItem;
export const assignPlanningTask = assignPlanningWorkItem;
export const startPlanningTaskExecution = startPlanningWorkItemExecution;
export const finishPlanningTaskExecution = finishPlanningWorkItemExecution;
export const addPlanningTaskEvidence = addPlanningWorkItemEvidence;
export const updatePlanningTaskEvidence = updatePlanningWorkItemEvidence;
export const deletePlanningTaskEvidence = deletePlanningWorkItemEvidence;
export const cancelPlanningTask = (
  workItemId: string,
  payload: PlanningCancelPayload,
) => cancelPlanningWorkItem(workItemId, payload);

export type StrategicPlanningImportQueued = {
  importId: string;
  hangfireJobId?: string | null;
  status: string;
  message: string;
};

export type StrategicPlanningImportStatus = {
  id: string;
  originalFileName: string;
  status: string;
  hangfireJobId?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errorSummary?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
};

/** Escopo do pai contextual da importação. */
export type StrategicPlanningImportScope =
  | 'Ciclo'
  | 'Objetivo'
  | 'Meta'
  | 'Marco'
  | 'MetaTarefas';

export async function downloadPlanningImportTemplate(
  scope: StrategicPlanningImportScope,
  parentId: string,
): Promise<Blob> {
  const response = await apiClient.get('/v1/strategic-planning/import/template', {
    params: { scope, parentId },
    responseType: 'blob',
  });
  return response.data;
}

export async function importStrategicPlanning(
  file: File,
  scope: StrategicPlanningImportScope,
  parentId: string,
): Promise<StrategicPlanningImportQueued> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<StrategicPlanningImportQueued>(
    '/v1/strategic-planning/import',
    formData,
    { params: { scope, parentId } },
  );
  return data;
}

export async function getStrategicPlanningImportStatus(
  importId: string,
): Promise<StrategicPlanningImportStatus> {
  const { data } = await apiClient.get<StrategicPlanningImportStatus>(
    `/v1/strategic-planning/import/${importId}`,
  );
  return data;
}

export async function exportStrategicPlanningCsv(cycleId?: string): Promise<Blob> {
  const response = await apiClient.get('/v1/strategic-planning/export.csv', {
    params: cycleId ? { cycleId } : undefined,
    responseType: 'blob',
  });
  return response.data;
}

export async function exportStrategicPlanningXlsx(cycleId?: string): Promise<Blob> {
  const response = await apiClient.get('/v1/strategic-planning/export.xlsx', {
    params: cycleId ? { cycleId } : undefined,
    responseType: 'blob',
  });
  return response.data;
}
